// src/components/DriverDetails.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  getDriverPictures,
  deletePicture,
  getDriverPicture,
  updatePictureStatusNew,
} from '../api';
import DriverPicture from './DriverPicture';
import DriverDetailsActionBar from './DriverDetailsActionBar';

const PictureGrid = ({
  pictures,
  imageUrls,
  onDelete,
  onStatusChange,
  onPrimaryChange,
  driverId,
  emptyMessage,
  showStatusBadge = true
}) => {
  if (pictures.length === 0) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '150px',
        mt: 2
      }}>
        <Typography variant="body1" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={1}>
      {pictures.map((picture) => (
        <Grid item xs={12} sm={6} md={4} key={picture.picture_uid}>
          <DriverPicture
            picture={picture}
            imageUrl={imageUrls[picture.picture_uid]}
            onDelete={() => onDelete(picture.picture_id)}
            onStatusChange={onStatusChange}
            onPrimaryChange={onPrimaryChange}
            driverId={driverId}
            showStatusBadge={showStatusBadge}
            pictureList={pictures}
            currentIndex={pictures.indexOf(picture)}
            imageUrls={imageUrls}
          />
        </Grid>
      ))}
    </Grid>
  );
};

const DriverDetails = ({ driver, driverStatus, token, onDriversUpdate }) => {
  const [pictures, setPictures] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [imageUrls, setImageUrls] = useState({});
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const fleetToken = sessionStorage.getItem('fleetToken');

  useEffect(() => {
    if (driver) {
      // Clear previous driver's pictures and images immediately
      setPictures([]);
      setImageUrls({});
      setError('');
      fetchPictures();
    }
  }, [driver]);

  const fetchPictures = useCallback(async () => {
    try {
      setLoading(true);
      const fetchedPictures = await getDriverPictures(fleetToken, driver.uid);
      setPictures(fetchedPictures);
      setError('');

      const urls = {};
      await Promise.all(
        fetchedPictures.map(async (picture) => {
          try {
            const imageUrl = await getDriverPicture(fleetToken, picture.picture_id);
            urls[picture.picture_uid] = imageUrl;
          } catch (err) {
            console.error('Error fetching image:', err);
            urls[picture.picture_uid] = null;
          }
        })
      );
      setImageUrls(urls);
    } catch (err) {
      setError(err.message || 'Failed to fetch driver pictures');
    } finally {
      setLoading(false);
    }
  }, [driver, fleetToken]);

  const handleDeletePicture = useCallback(async (pictureId) => {
    try {
      setLoading(true);
      await deletePicture(fleetToken, pictureId);
      await fetchPictures();
      setSnackbarMessage('Picture deleted successfully');
      setShowSnackbar(true);
    } catch (err) {
      setError(err.message || 'Failed to delete picture');
      onDriversUpdate(); // Refresh driver list on delete failure
    } finally {
      setLoading(false);
    }
  }, [fleetToken, fetchPictures, onDriversUpdate]);



  const handlePictureStatusChange = useCallback((pictureId, newStatus) => {
    try {
      console.log('handlePictureStatusChange called with:', pictureId, newStatus);
      // Update the local state immediately for better UX
      setPictures(prevPictures => {
        console.log('Updating pictures for status change');
        return prevPictures.map(picture => {
          if (Number(picture.picture_id) === Number(pictureId)) {
            console.log('Updating picture', picture.picture_id, 'status from', picture.picture_status, 'to', newStatus);
            return { ...picture, picture_status: newStatus };
          }
          return picture;
        });
      });

      setSnackbarMessage('Picture status updated successfully');
      setShowSnackbar(true);
    } catch (err) {
      console.error('Error updating picture status:', err);
      // The error will be handled by the PictureStatusChip component
    }
  }, []);

  const handlePrimaryChange = useCallback((pictureId) => {
    try {
      console.log('handlePrimaryChange called with pictureId:', pictureId);

      // Update the local state to reflect the new primary picture
      setPictures(prevPictures => {
        console.log('Updating pictures, prevPictures:', prevPictures);
        const updatedPictures = prevPictures.map(picture => {
          const isPrimary = Number(picture.picture_id) === Number(pictureId);
          console.log(`Picture ${picture.picture_id}: setting is_primary to ${isPrimary}`);
          // Create a completely new object to ensure React detects the change
          return {
            ...picture,
            is_primary: isPrimary,
            _lastUpdated: Date.now() // Force re-render
          };
        });
        console.log('Updated pictures:', updatedPictures);
        return updatedPictures;
      });

      setSnackbarMessage('Primary picture updated successfully');
      setShowSnackbar(true);
    } catch (err) {
      console.error('Error updating primary picture:', err);
    }
  }, []);

  const handleBulkStatusUpdate = useCallback(async (pictures, newStatus, actionName) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('fleetToken');
      let successCount = 0;
      let errorCount = 0;

      // Process pictures sequentially
      for (const picture of pictures) {
        try {
          await updatePictureStatusNew(token, picture.picture_id, newStatus);
          // Update local state immediately for better UX
          setPictures(prevPictures =>
            prevPictures.map(p =>
              p.picture_id === picture.picture_id
                ? { ...p, picture_status: newStatus }
                : p
            )
          );
          successCount++;
        } catch (err) {
          console.error(`Error updating picture ${picture.picture_id}:`, err);
          errorCount++;
        }
      }

      // Show appropriate message based on results
      if (errorCount === 0) {
        setSnackbarMessage(`All ${pictures.length} pictures marked as ${actionName} successfully`);
      } else if (successCount === 0) {
        setError(`Failed to update any pictures`);
      } else {
        setSnackbarMessage(`${successCount} pictures marked as ${actionName}, ${errorCount} failed`);
      }
      setShowSnackbar(true);
    } catch (err) {
      setError(`Failed to perform bulk ${actionName.toLowerCase()} operation`);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleBulkDelete = useCallback(async (pictures) => {
    try {
      setLoading(true);
      const token = sessionStorage.getItem('fleetToken');
      let successCount = 0;
      let errorCount = 0;

      // Process pictures sequentially
      for (const picture of pictures) {
        try {
          await deletePicture(token, picture.picture_id);
          // Update local state by removing the picture
          setPictures(prevPictures =>
            prevPictures.filter(p => p.picture_id !== picture.picture_id)
          );
          successCount++;
        } catch (err) {
          console.error(`Error deleting picture ${picture.picture_id}:`, err);
          errorCount++;
        }
      }

      // Show appropriate message based on results
      if (errorCount === 0) {
        setSnackbarMessage(`All ${pictures.length} pictures acknowledged successfully`);
      } else if (successCount === 0) {
        setError('Failed to acknowledge any pictures');
      } else {
        setSnackbarMessage(`${successCount} pictures acknowledged, ${errorCount} failed`);
      }
      setShowSnackbar(true);

      // Refresh pictures to ensure UI is in sync
      await fetchPictures();
    } catch (err) {
      setError('Failed to perform bulk acknowledge operation');
    } finally {
      setLoading(false);
    }
  }, [fetchPictures]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getPicturesByCategory = (category) => {
    switch (category) {
      case 'recognition':
        return pictures.filter(picture => [0, 20, 30].includes(picture.picture_status));
      case 'successfullyIdentified':
        return pictures.filter(picture => picture.picture_status === 40);
      case 'identityFailures':
        return pictures.filter(picture => [50, 60].includes(picture.picture_status));
      case 'statistics':
        return pictures.filter(picture => [51, 52, 61, 62].includes(picture.picture_status));
      default:
        return [];
    }
  };

  if (!driver) {
    return null;
  }

  return (
    <Box sx={{ p: 1, height: '100%', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" component="h2" gutterBottom>
          {driver.name} {driver.surnames}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          UID: {driver.uid}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          CORP code: {driver.code || 'N/A'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Fleet: {driver.nameOperator}
        </Typography>
      </Paper>

      <Box sx={{ mt: 1 }}>
        <DriverDetailsActionBar
          driver={driver}
          driverStatus={driverStatus}
        />
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {pictures.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Recognition (${getPicturesByCategory('recognition').length})`} />
            <Tab label={`Successfully identified (${getPicturesByCategory('successfullyIdentified').length})`} />
            <Tab label={`Identity failures (${getPicturesByCategory('identityFailures').length})`} />
            <Tab label={`Statistics (${getPicturesByCategory('statistics').length})`} />
          </Tabs>

          <Box sx={{ mt: 2 }}>
            {activeTab === 0 && (
              <>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    onClick={() => handleBulkStatusUpdate(
                      getPicturesByCategory('recognition').filter(p => p.picture_status !== 30),
                      30,
                      'Valid'
                    )}
                    disabled={loading || getPicturesByCategory('recognition').filter(p => p.picture_status !== 30).length === 0}
                  >
                    Mark All Valid
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    onClick={() => handleBulkStatusUpdate(
                      getPicturesByCategory('recognition').filter(p => p.picture_status !== 20),
                      20,
                      'Invalid'
                    )}
                    disabled={loading || getPicturesByCategory('recognition').filter(p => p.picture_status !== 20).length === 0}
                  >
                    Mark All Invalid
                  </Button>
                </Stack>
                <PictureGrid
                  pictures={getPicturesByCategory('recognition')}
                  imageUrls={imageUrls}
                  onDelete={handleDeletePicture}
                  onStatusChange={handlePictureStatusChange}
                  onPrimaryChange={handlePrimaryChange}
                  driverId={driver.uid}
                  emptyMessage="No pictures in recognition process"
                  showStatusBadge={true}
                />
              </>
            )}

            {activeTab === 1 && (
              <PictureGrid
                pictures={getPicturesByCategory('successfullyIdentified')}
                imageUrls={imageUrls}
                onDelete={handleDeletePicture}
                onStatusChange={handlePictureStatusChange}
                onPrimaryChange={handlePrimaryChange}
                driverId={driver.uid}
                emptyMessage="No successfully identified pictures"
                showStatusBadge={true}
              />
            )}

            {activeTab === 2 && (
              <>
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    onClick={() => {
                      const primaryPicture = pictures.find(p => p.is_primary);
                      const identityFailures = getPicturesByCategory('identityFailures');
                      // Exclude primary picture from bulk delete if it exists in failures
                      const failuresToDelete = primaryPicture
                        ? identityFailures.filter(p => p.picture_id !== primaryPicture.picture_id)
                        : identityFailures;
                      handleBulkDelete(failuresToDelete);
                    }}
                    disabled={loading || getPicturesByCategory('identityFailures').length === 0}
                  >
                    Remove All
                  </Button>
                </Box>

                {/* Display primary picture at the top */}
                {(() => {
                  const primaryPicture = pictures.find(p => p.is_primary);
                  const identityFailures = getPicturesByCategory('identityFailures');
                  const primaryInFailures = identityFailures.find(p => p.picture_id === primaryPicture?.picture_id);

                  // Filter out primary picture from failures if it's displayed at top
                  const filteredFailures = primaryInFailures
                    ? identityFailures.filter(p => p.picture_id !== primaryPicture.picture_id)
                    : identityFailures;

                  return (
                    <>
                      {primaryPicture ? (
                        <>
                          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Primary picture (as reference)
                          </Typography>
                          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                            <DriverPicture
                              picture={primaryPicture}
                              imageUrl={imageUrls[primaryPicture.picture_uid]}
                              onDelete={undefined} // Disable delete for primary in this context
                              onStatusChange={undefined} // Disable status change for primary in this context
                              onPrimaryChange={undefined} // Disable primary change for primary in this context
                              showStatusBadge={false} // Hide status badge for read-only primary picture
                              driverId={driver.uid}
                              pictureList={[primaryPicture]} // Single picture list for primary picture
                              currentIndex={0}
                              imageUrls={imageUrls}
                            />
                          </Box>
                          <Divider sx={{ mb: 2 }} />
                        </>
                      ) : null}

                      <PictureGrid
                        pictures={filteredFailures}
                        imageUrls={imageUrls}
                        onDelete={handleDeletePicture}
                        onStatusChange={handlePictureStatusChange}
                        onPrimaryChange={handlePrimaryChange}
                        driverId={driver.uid}
                        emptyMessage="No identity failure pictures"
                        showStatusBadge={true}
                      />
                    </>
                  );
                })()}
              </>
            )}

            {activeTab === 3 && (
              <PictureGrid
                pictures={getPicturesByCategory('statistics')}
                imageUrls={imageUrls}
                onDelete={handleDeletePicture}
                onStatusChange={handlePictureStatusChange}
                onPrimaryChange={handlePrimaryChange}
                driverId={driver.uid}
                emptyMessage="No statistics pictures"
                showStatusBadge={true}
              />
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '200px',
          mt: 2
        }}>
          <Typography variant="h6" color="text.secondary">
            No images available for this driver
          </Typography>
        </Box>
      )}

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSnackbar(false)}
      >
        <Alert
          onClose={() => setShowSnackbar(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DriverDetails;
